<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { marked } from 'marked'
import AppComponent from '@/views/base/AppComponent.vue'
import { useHelpArticles } from '@/composables/useHelpArticles'

const route = useRoute()
const router = useRouter()
const { getArticle } = useHelpArticles()

const article = computed(() => {
  const slug = route.params.slug as string
  return getArticle(slug)
})

function escapeAttr(s: string): string {
  return s.replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

function isSafeUrl(url: string): boolean {
  return url.startsWith('/') || url.startsWith('https://')
}

// ponytail: robust YAML parser for code blocks
function parseYaml(yaml: string): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  const lines = yaml.split('\n')
  let i = 0

  while (i < lines.length) {
    const line = lines[i]
    const trimmed = line.trim()
    if (!trimmed) {
      i++
      continue
    }

    // Top-level key
    const keyMatch = trimmed.match(/^(\w+):(.*)$/)
    if (keyMatch) {
      const key = keyMatch[1]
      const value = keyMatch[2].trim().replace(/^["']|["']$/g, '')

      if (value) {
        result[key] = value
        i++
      } else {
        // It's a list - parse items
        const items: Record<string, string>[] = []
        i++
        while (i < lines.length) {
          const itemLine = lines[i]
          if (!itemLine.trim()) {
            i++
            continue
          }
          if (!itemLine.startsWith('  ')) break // Back to top level

          if (itemLine.trim().startsWith('- ')) {
            items.push({})
            // Check if there are inline properties on same line
            const inlineMatch = itemLine.trim().match(/^- (\w+):(.*)$/)
            if (inlineMatch) {
              items[items.length - 1][inlineMatch[1]] = inlineMatch[2]
                .trim()
                .replace(/^["']|["']$/g, '')
            }
            i++
          } else if (items.length > 0) {
            // Property of current item
            const propMatch = itemLine.trim().match(/^(\w+):(.*)$/)
            if (propMatch) {
              items[items.length - 1][propMatch[1]] = propMatch[2]
                .trim()
                .replace(/^["']|["']$/g, '')
            }
            i++
          } else {
            break
          }
        }
        result[key] = items
      }
    } else {
      i++
    }
  }
  return result
}

function renderTimeline(yaml: string): string {
  const data = parseYaml(yaml)
  const items = (data.items as { date?: string; title?: string; content?: string }[]) || []
  const style = (data.style as string) || 'italic' // bold, italic, normal

  const itemsHtml = items
    .map((item) => {
      const dateClass =
        style === 'bold' ? 'timeline-date--bold' : style === 'normal' ? 'timeline-date--normal' : ''
      const titleHtml = item.title ? `<strong>${item.title}</strong> ` : ''
      return `<div class="timeline-item"><div class="timeline-date ${dateClass}">${item.date || ''}</div><div class="timeline-content">${titleHtml}${item.content || ''}</div></div>`
    })
    .join('')
  return `<div class="timeline">${itemsHtml}</div>`
}

const TAG_COLORS: Record<string, { text: string; bg: string }> = {
  green: { text: '#059669', bg: '#ecfdf5' },
  blue: { text: '#2563eb', bg: '#eff6ff' },
  amber: { text: '#d97706', bg: '#fffbeb' },
  gray: { text: '#6b7280', bg: '#f3f4f6' },
  red: { text: '#dc2626', bg: '#fef2f2' },
}

function renderCartouche(yaml: string): string {
  const data = parseYaml(yaml)
  const items = (data.items as { emoji?: string; title?: string; content?: string }[]) || []
  const tagColor = TAG_COLORS[(data.tagColor as string) || 'green'] || TAG_COLORS.green

  const itemsHtml = items
    .map(
      (item) =>
        `<div class="cartouche-item"><span class="cartouche-emoji">${item.emoji || ''}</span><div class="cartouche-item-content"><strong>${item.title || ''}</strong> ${item.content || ''}</div></div>`,
    )
    .join('')
  return `<div class="cartouche"><div class="cartouche-header"><div class="cartouche-title"><span class="cartouche-name">${data.title || ''}</span><span class="cartouche-subtitle">${data.subtitle || ''}</span></div><span class="cartouche-tag" style="color: ${tagColor.text}; background: ${tagColor.bg}; border-color: ${tagColor.text}">${data.tag || ''}</span></div><div class="cartouche-items">${itemsHtml}</div></div>`
}

function renderCallout(yaml: string): string {
  const data = parseYaml(yaml)
  const color = (data.color as string) || 'amber'
  const colorMap: Record<string, string> = {
    amber: '#f5a623',
    yellow: '#f5a623',
    blue: '#3b82f6',
    cyan: '#06b6d4',
    red: '#ef4444',
    green: '#22c55e',
  }
  const borderColor = colorMap[color] || colorMap.amber
  const bgColor =
    color === 'blue' || color === 'cyan' ? 'rgba(59,130,246,0.05)' : 'rgba(245,166,35,0.08)'

  return `<div class="callout" style="--callout-color: ${borderColor}; --callout-bg: ${bgColor}"><span class="callout-emoji">${data.emoji || ''}</span><div class="callout-content"><strong>${data.title || ''}</strong> ${data.content || ''}</div></div>`
}

interface TableRow {
  platform?: string
  badge?: string
  since?: string
  metric?: string
  metricColor?: string
  formats?: string
  audience?: string
  limitation?: string
}

function renderTable(yaml: string): string {
  const data = parseYaml(yaml)
  const rows = (data.rows as TableRow[]) || []

  const rowsHtml = rows
    .map((row) => {
      const metricColor = TAG_COLORS[row.metricColor || 'green'] || TAG_COLORS.green
      const badgeHtml = row.badge ? `<span class="table-badge">${row.badge}</span>` : ''
      const limitHtml = row.limitation
        ? `<span class="table-limit">⚠️ ${row.limitation}</span>`
        : ''

      return `<tr>
      <td><strong>${row.platform || ''}</strong>${badgeHtml}</td>
      <td>${row.since || ''}</td>
      <td><span class="table-tag" style="color: ${metricColor.text}; background: ${metricColor.bg}">${row.metric || ''}</span></td>
      <td>${row.formats || ''}</td>
      <td>${row.audience || 'N/A'}</td>
      <td>${limitHtml}</td>
    </tr>`
    })
    .join('')

  return `<table class="rich-table"><thead><tr><th>Platform</th><th>Tracked Since</th><th>Metric</th><th>Ad Formats</th><th>Audience Detail</th><th>Key Limitation</th></tr></thead><tbody>${rowsHtml}</tbody></table>`
}

interface CardTag {
  text?: string
  color?: string
}

function renderCard(yaml: string): string {
  const data = parseYaml(yaml)
  const tags = (data.tags as CardTag[]) || []

  const tagsHtml = tags
    .map((tag) => {
      const color = TAG_COLORS[tag.color || 'gray'] || TAG_COLORS.gray
      return `<span class="card-tag" style="color: ${color.text}; background: ${color.bg}">${tag.text || ''}</span>`
    })
    .join('')

  const linkHtml =
    data.link && data.linkUrl && isSafeUrl(data.linkUrl as string)
      ? `<a href="${escapeAttr(data.linkUrl as string)}" class="card-link">${data.link}</a>`
      : ''

  return `<div class="geo-card"><div class="geo-number">${data.number || ''}</div><div class="geo-legend">${data.legend || ''}</div><h3 class="geo-title">${data.title || ''}</h3><p class="geo-content">${data.content || ''}</p>${linkHtml}<div class="geo-tags">${tagsHtml}</div></div>`
}

function renderSectionImage(block: string): string {
  const [meta, ...contentParts] = block.split('\n---\n')
  const data = parseYaml(meta)
  let content = contentParts.join('\n---\n')
  const position = data.position || 'right'
  const image = String(data.image || '')

  // Process nested custom blocks (using ~~~ instead of ``` inside section-image)
  content = content.replace(/~~~callout\n([\s\S]*?)~~~/g, (_, yaml) => renderCallout(yaml))

  // Process images with size before marked()
  content = content.replace(/!\[([^|\]]*)\|(\d+)\]\(([^)]+)\)/g, (_, alt, width, url) => {
    return `<div class="img-container"><img class="help-img" src="${escapeAttr(url)}" alt="${escapeAttr(alt)}" style="max-width: ${width}px" /></div>`
  })

  const contentHtml = marked(content) as string
  const flexDirection = position === 'left' ? 'row-reverse' : 'row'

  return `<div class="section-image" style="flex-direction: ${flexDirection}">
    <div class="section-image-content">${contentHtml}</div>
    <div class="section-image-img"><img class="help-img" src="${escapeAttr(image)}" alt="" /></div>
  </div>`
}

const html = computed(() => {
  if (!article.value) return ''

  let content = article.value.content

  // Process custom blocks
  content = content.replace(/```timeline\n([\s\S]*?)```/g, (_, yaml) => renderTimeline(yaml))
  content = content.replace(/```cartouche\n([\s\S]*?)```/g, (_, yaml) => renderCartouche(yaml))
  content = content.replace(/```platform\n([\s\S]*?)```/g, (_, yaml) => renderCartouche(yaml)) // legacy alias
  content = content.replace(/```callout\n([\s\S]*?)```/g, (_, yaml) => renderCallout(yaml))
  content = content.replace(/```table\n([\s\S]*?)```/g, (_, yaml) => renderTable(yaml))
  content = content.replace(/```card\n([\s\S]*?)```/g, (_, yaml) => renderCard(yaml))
  content = content.replace(/```section-image\n([\s\S]*?)```/g, (_, block) =>
    renderSectionImage(block),
  )

  // Process images with size: ![alt|width](url) -> centered image with max-width
  content = content.replace(/!\[([^|\]]*)\|(\d+)\]\(([^)]+)\)/g, (_, alt, width, url) => {
    return `<div class="img-container"><img class="help-img" src="${escapeAttr(url)}" alt="${escapeAttr(alt)}" style="max-width: ${width}px" /></div>`
  })

  return marked(content) as string
})

if (!article.value) {
  router.replace('/help')
}
</script>

<template>
  <AppComponent :title="article?.title ?? 'Help'" id="help-article">
    <div v-if="article" class="help-article">
      <component
        :is="article.listed ? 'RouterLink' : 'button'"
        :to="article.listed ? '/help' : undefined"
        class="back-link"
        @click="!article.listed && router.back()"
      >
        <UIcon name="i-lucide-arrow-left" class="size-4" />
        {{ article.listed ? 'Back to Help Center' : 'Back' }}
      </component>

      <h1 class="page-title">{{ article.title }}</h1>
      <p class="page-subtitle">{{ article.description }}</p>
      <p class="page-meta">Last updated: {{ article.lastUpdated }}</p>

      <article class="prose" v-html="html" />
    </div>
  </AppComponent>
</template>

<style scoped>
.help-article {
  max-width: 900px;
  margin: 0 auto;
  padding: 48px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--ui-text-muted);
  margin-bottom: 32px;
  transition: color 0.15s;
}
.back-link:hover {
  color: var(--ui-text);
}

.page-title {
  font-size: 42px;
  font-weight: 800;
  color: var(--ui-text-highlighted);
  line-height: 1.15;
  letter-spacing: -0.02em;
  margin-bottom: 16px;
}

.page-subtitle {
  font-size: 17px;
  color: var(--ui-text-muted);
  margin-bottom: 8px;
  line-height: 1.65;
  max-width: 640px;
}

.page-meta {
  font-size: 11px;
  font-family: ui-monospace, monospace;
  color: var(--ui-text-dimmed);
  margin-bottom: 48px;
  padding-bottom: 32px;
  border-bottom: 1px solid var(--ui-border);
  margin-top: 8px;
}

/* Section titles (## headings) */
.prose :deep(h2) {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid var(--ui-border);
  background: var(--ui-bg);
  font-family: ui-monospace, monospace;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #a66907;
  margin-top: 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgb(13 27 42 / 0.06);
}
.prose :deep(h2)::before {
  content: '';
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f5a623;
  flex-shrink: 0;
}

.prose :deep(h3) {
  font-size: 18px;
  font-weight: 700;
  color: var(--ui-text-highlighted);
  margin-top: 24px;
  margin-bottom: 10px;
  letter-spacing: -0.01em;
}

.prose :deep(p) {
  font-size: 15px;
  color: var(--ui-text-muted);
  margin-bottom: 14px;
  line-height: 1.75;
}

/* Table styling */
.prose :deep(table) {
  width: 100%;
  font-size: 13px;
  margin: 20px 0;
  border-radius: 12px;
  border: 1px solid var(--ui-border);
  overflow: hidden;
  box-shadow:
    0 1px 0 rgb(15 28 46 / 0.04),
    0 12px 32px -12px rgb(15 28 46 / 0.18);
}
.prose :deep(thead th) {
  background: var(--ui-bg-elevated);
  border-bottom: 1px solid var(--ui-border);
  padding: 11px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ui-text-muted);
}
.prose :deep(td) {
  padding: 12px 16px;
  border-bottom: 1px solid var(--ui-border);
  color: var(--ui-text);
}
.prose :deep(tr:last-child td) {
  border-bottom: none;
}
.prose :deep(tr:hover td) {
  background: var(--ui-bg-elevated);
}

/* Timeline */
.prose :deep(.timeline) {
  margin: 20px 0;
}
.prose :deep(.timeline-item) {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 32px;
  padding: 16px 0;
  border-bottom: 1px solid var(--ui-border);
}
.prose :deep(.timeline-item:last-child) {
  border-bottom: none;
}
.prose :deep(.timeline-date) {
  font-family: ui-monospace, monospace;
  font-size: 13px;
  color: var(--ui-text-muted);
  font-style: italic;
}
.prose :deep(.timeline-date--bold) {
  font-style: normal;
  font-weight: 600;
  color: var(--ui-text-highlighted);
}
.prose :deep(.timeline-date--normal) {
  font-style: normal;
}
.prose :deep(.timeline-content) {
  font-size: 15px;
  color: var(--ui-text-muted);
  line-height: 1.65;
}
.prose :deep(.timeline-content strong) {
  background: none;
  padding: 0;
}

/* Cartouche */
.prose :deep(.cartouche) {
  background: var(--ui-bg);
  border: 1px solid var(--ui-border);
  border-radius: 12px;
  margin: 20px 0;
  overflow: hidden;
}
.prose :deep(.cartouche-header) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--ui-border);
  background: var(--ui-bg-elevated);
}
.prose :deep(.cartouche-title) {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.prose :deep(.cartouche-name) {
  font-size: 16px;
  font-weight: 700;
  color: var(--ui-text-highlighted);
}
.prose :deep(.cartouche-subtitle) {
  font-size: 13px;
  font-family: ui-monospace, monospace;
  color: var(--ui-text-muted);
}
.prose :deep(.cartouche-tag) {
  font-size: 12px;
  font-family: ui-monospace, monospace;
  padding: 4px 12px;
  border-radius: 999px;
  color: var(--tag-color);
  background: var(--tag-bg);
  border: 1px solid var(--tag-color);
}
.prose :deep(.cartouche-items) {
  padding: 16px 20px;
}
.prose :deep(.cartouche-item) {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--ui-border);
}
.prose :deep(.cartouche-item:last-child) {
  border-bottom: none;
  padding-bottom: 0;
}
.prose :deep(.cartouche-item:first-child) {
  padding-top: 0;
}
.prose :deep(.cartouche-emoji) {
  font-size: 18px;
  line-height: 1.4;
}
.prose :deep(.cartouche-item-content) {
  font-size: 14px;
  color: var(--ui-text-muted);
  line-height: 1.65;
}
.prose :deep(.cartouche-item-content strong) {
  background: none;
  padding: 0;
}

/* Callout */
.prose :deep(.callout) {
  display: flex;
  gap: 12px;
  background: var(--callout-bg);
  border: 1px solid var(--ui-border);
  border-left: 3px solid var(--callout-color);
  border-radius: 10px;
  padding: 16px 20px;
  margin: 16px 0;
}
.prose :deep(.callout-emoji) {
  font-size: 18px;
  line-height: 1.4;
}
.prose :deep(.callout-content) {
  font-size: 14px;
  color: var(--ui-text);
  line-height: 1.65;
}
.prose :deep(.callout-content strong) {
  background: none;
  padding: 0;
}

/* Blockquotes as simple callouts (fallback) */
.prose :deep(blockquote) {
  background: var(--ui-bg-elevated);
  border: 1px solid var(--ui-border);
  border-left: 3px solid #f5a623;
  border-radius: 10px;
  padding: 16px 20px;
  font-size: 14px;
  color: var(--ui-text);
  margin: 16px 0;
  line-height: 1.65;
}
.prose :deep(blockquote p) {
  margin: 0;
  color: inherit;
}

.prose :deep(code) {
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 6px;
  background: var(--ui-bg-elevated);
  border: 1px solid var(--ui-border);
  font-family: ui-monospace, monospace;
  color: var(--ui-text-muted);
}

/* Hide horizontal rules */
.prose :deep(hr) {
  display: none;
}

/* List styling with custom bullets */
.prose :deep(ul) {
  list-style: none;
  padding-left: 0;
  margin-bottom: 16px;
}
.prose :deep(ul li) {
  position: relative;
  padding-left: 24px;
  font-size: 15px;
  color: var(--ui-text-muted);
  margin-bottom: 8px;
  line-height: 1.65;
}
.prose :deep(ul li)::before {
  content: '';
  position: absolute;
  left: 0;
  top: 10px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #f5a623;
}

/* Bold text as inline highlights */
.prose :deep(strong) {
  font-weight: 600;
  color: var(--ui-text-highlighted);
  background: linear-gradient(transparent 60%, rgb(245 166 35 / 0.2) 60%);
  padding: 0 2px;
}

/* Links */
.prose :deep(a) {
  color: #f5a623;
  text-decoration: none;
  border-bottom: 1px solid rgb(245 166 35 / 0.3);
  transition: border-color 0.15s;
}
.prose :deep(a:hover) {
  border-bottom-color: #f5a623;
}

/* Rich table */
.prose :deep(.rich-table) {
  width: 100%;
  font-size: 13px;
  margin: 20px 0;
  border-radius: 12px;
  border: 1px solid var(--ui-border);
  border-collapse: separate;
  border-spacing: 0;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgb(15 28 46 / 0.04),
    0 12px 32px -12px rgb(15 28 46 / 0.18);
}
.prose :deep(.rich-table thead th) {
  background: var(--ui-bg-elevated);
  border-bottom: 1px solid var(--ui-border);
  padding: 11px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ui-text-muted);
}
.prose :deep(.rich-table td) {
  padding: 14px 16px;
  border-bottom: 1px solid var(--ui-border);
  color: var(--ui-text);
  vertical-align: top;
}
.prose :deep(.rich-table tr:last-child td) {
  border-bottom: none;
}
.prose :deep(.rich-table tr:hover td) {
  background: var(--ui-bg-elevated);
}
.prose :deep(.rich-table td strong) {
  background: none;
  padding: 0;
  color: var(--ui-text-highlighted);
}
.prose :deep(.table-badge) {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  background: #f59e0b;
  color: white;
}
.prose :deep(.table-tag) {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  font-family: ui-monospace, monospace;
}
.prose :deep(.table-limit) {
  font-size: 12px;
  color: var(--ui-text-muted);
}

/* Geographic card */
.prose :deep(.geo-card) {
  background: var(--ui-bg);
  border: 1px solid var(--ui-border);
  border-radius: 12px;
  padding: 24px;
  margin: 20px 0;
}
.prose :deep(.geo-number) {
  font-size: 48px;
  font-weight: 800;
  color: var(--ui-text-highlighted);
  line-height: 1;
  margin-bottom: 4px;
}
.prose :deep(.geo-legend) {
  font-size: 13px;
  font-family: ui-monospace, monospace;
  color: var(--ui-text-muted);
  margin-bottom: 20px;
}
.prose :deep(.geo-title) {
  font-size: 18px;
  font-weight: 700;
  color: var(--ui-text-highlighted);
  margin: 0 0 12px 0;
}
.prose :deep(.geo-content) {
  font-size: 14px;
  color: var(--ui-text-muted);
  line-height: 1.65;
  margin-bottom: 16px;
}
.prose :deep(.card-link) {
  display: inline-block;
  font-size: 13px;
  font-weight: 500;
  font-family: ui-monospace, monospace;
  color: #d97706;
  margin-bottom: 16px;
}
.prose :deep(.geo-tags) {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.prose :deep(.card-tag) {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-family: ui-monospace, monospace;
}

/* Shared image styling */
.prose :deep(.help-img) {
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
}

/* Centered images */
.prose :deep(.img-container) {
  display: flex;
  justify-content: center;
  margin: 24px 0;
}

/* Section with side image */
.prose :deep(.section-image) {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  margin: 32px 0;
}
.prose :deep(.section-image-content) {
  flex: 1;
  min-width: 0;
}
.prose :deep(.section-image-img) {
  flex-shrink: 0;
  width: 320px;
}
.prose :deep(.section-image-img img) {
  width: 100%;
  height: auto;
}
</style>
